import {
  Configuration,
  Curve,
  MediaType,
  Obstacle,
  Point,
  TrajectoryGroup,
} from '../types/sharedTypes';
import { CollisionDetectionService } from './collisionDetectionService';
import { ConfigurationService } from './configurationService';

export type ExtendedTrajectoryData = {
  trajectoryGroup: TrajectoryGroup;
  recoilInNewton: number;
  impactAreaDiameter: number;
  range: number;
  highestPoint: Point;
  didCutOffOccur: boolean;
};

export type CalculateTrajactoryInput = {
  monitorId: string;
  nozzleId: string;
  flowRate: number;
  pressure: number;
  angle: number;
  monitorHeight: number;
  mediaType: MediaType;
  obstacles: Array<Obstacle>;
};

type TrajectoryTypeCorrectionFactorInput = {
  trajectoryTypeCorrectionFactorX: number;
  trajectoryTypeCorrectionFactorY: number;
};

type CalculateRecoilInput = {
  flowRateInLiterPerMinute: number;
  pressureInBar: number;
};

type CalculateImpactAreaInput = {
  maxFootprintTrajectory: Curve;
  minFootprintTrajectory: Curve;
};

type StopTrajectoriesAtObstaclesInput = {
  trajectoryGroup: TrajectoryGroup;
  obstacles: Array<Obstacle>;
};

export class TrajectoryCalculationService {
  private configurationService: ConfigurationService;

  private collisionDetectionService: CollisionDetectionService;

  constructor(private configuration: Configuration) {
    this.configurationService = new ConfigurationService(configuration);
    this.collisionDetectionService = new CollisionDetectionService();
  }

  public calculateExtendedTrajectoryData(
    calculateTrajectoryInput: CalculateTrajactoryInput
  ): ExtendedTrajectoryData | null {
    try {
      const trajectoryGroup: TrajectoryGroup = {
        mainTrajectory: this.calculateMainTrajectory(calculateTrajectoryInput),
        minFootprintTrajectory: this.calculateMinFootprintTrajectory(
          calculateTrajectoryInput
        ),
        maxFootprintTrajectory: this.calculateMaxFootprintTrajectory(
          calculateTrajectoryInput
        ),
      };

      const recoilInNewton = this.calculateRecoilInNewton({
        flowRateInLiterPerMinute: calculateTrajectoryInput.flowRate,
        pressureInBar: calculateTrajectoryInput.pressure,
      });
      const impactAreaDiameter = this.calculateImpactAreaDiameter({
        minFootprintTrajectory: trajectoryGroup.minFootprintTrajectory,
        maxFootprintTrajectory: trajectoryGroup.maxFootprintTrajectory,
      });
      const range = this.getRangeFromTrajectories([
        trajectoryGroup.mainTrajectory,
        trajectoryGroup.minFootprintTrajectory,
        trajectoryGroup.maxFootprintTrajectory,
      ]);
      const highestPoint = this.getHighestPointFromTrajectories([
        trajectoryGroup.mainTrajectory,
        trajectoryGroup.minFootprintTrajectory,
        trajectoryGroup.maxFootprintTrajectory,
      ]);

      const didCutOffOccur = this.cutOffTrajectoriesAtObstacles({
        trajectoryGroup,
        obstacles: calculateTrajectoryInput.obstacles,
      });

      return {
        trajectoryGroup,
        recoilInNewton,
        impactAreaDiameter,
        range,
        highestPoint,
        didCutOffOccur,
      };
    } catch {
      return null;
    }
  }

  public calculateMainTrajectory(
    calculateTrajectoryInput: CalculateTrajactoryInput
  ): Curve {
    const trajectoryTypeCorrectionFactorX = 1.0;
    const trajectoryTypeCorrectionFactorY = 1.0;
    return this.calculateTrajectory({
      ...calculateTrajectoryInput,
      trajectoryTypeCorrectionFactorX,
      trajectoryTypeCorrectionFactorY,
    });
  }

  public calculateMinFootprintTrajectory(
    calculateTrajectoryInput: CalculateTrajactoryInput
  ): Curve {
    const trajectoryTypeCorrectionFactorX =
      this.configuration.global.footprint.correctionFactorMinX;
    const trajectoryTypeCorrectionFactorY =
      this.configuration.global.footprint.correctionFactorMinY;
    return this.calculateTrajectory({
      ...calculateTrajectoryInput,
      trajectoryTypeCorrectionFactorX,
      trajectoryTypeCorrectionFactorY,
    });
  }

  public calculateMaxFootprintTrajectory(
    calculateTrajectoryInput: CalculateTrajactoryInput
  ): Curve {
    const trajectoryTypeCorrectionFactorX =
      this.configuration.global.footprint.correctionFactorMaxX;
    const trajectoryTypeCorrectionFactorY =
      this.configuration.global.footprint.correctionFactorMaxY;
    return this.calculateTrajectory({
      ...calculateTrajectoryInput,
      trajectoryTypeCorrectionFactorX,
      trajectoryTypeCorrectionFactorY,
    });
  }

  protected calculateTrajectory({
    monitorId,
    nozzleId,
    flowRate,
    pressure,
    angle,
    monitorHeight,
    mediaType,
    trajectoryTypeCorrectionFactorX,
    trajectoryTypeCorrectionFactorY,
  }: CalculateTrajactoryInput & TrajectoryTypeCorrectionFactorInput): Curve {
    /*
        X = X0 * TX * DX* MX * VX * PX * aX
        Y = Y0 * TY * DY* MY * VY * PY * aY

        X0: X Wert aus der Masterkurve
        Y0: Y Wert aus der Masterkurve
        T: Korrekturfaktor Monitor
        D: Korrekturfaktor Düse
        M: Korrekturfaktor Medientyp
        V: Korrekturfaktor Volumenstrom
        P: Korrekturfaktor Druck
        a: Korrekturfaktor Winkel
        */

    const monitor = this.configuration.monitors?.find(
      (e) => e.id === monitorId
    );
    if (!monitor) {
      throw new Error(
        `Could not find monitor with id ${monitorId} in configuration!`
      );
    }

    const nozzle = this.configuration.nozzles?.find((e) => e.id === nozzleId);
    if (!nozzle) {
      throw new Error(
        `Could not find nozzle with id ${nozzleId} in configuration!`
      );
    }

    const medium = this.configuration.global.mediaTypes[mediaType];

    const angleConfig =
      this.configurationService.findAngleConfigurationForGivenAngle(angle);
    if (!angleConfig) {
      throw new Error(
        `Could not find angle configuration for angle ${angle} in configuration!`
      );
    }

    const flowRateConfig =
      this.configurationService.findFlowRateConfigurationForGivenFlowRate(
        flowRate
      );
    if (!flowRateConfig) {
      throw new Error(
        `Could not find flow rate configuration for flow rate ${flowRate} in configuration!`
      );
    }

    const pressureConfig =
      this.configurationService.findPressureConfigurationForGivenPressure(
        pressure
      );
    if (!pressureConfig) {
      throw new Error(
        `Could not find flow rate configuration for pressure ${pressure} in configuration!`
      );
    }

    const masterCurve =
      angle >= this.configuration.global.angleStep
        ? this.configuration.global.masterCurveForPositiveAngles
        : this.configuration.global.masterCurveForNegativeAngles;
    if (!masterCurve) {
      throw new Error(
        `Could not find master curve for ${
          angle >= this.configuration.global.angleStep
            ? 'positive angles'
            : 'negative angles'
        } in configuration!`
      );
    }

    const trajectory = masterCurve.map((point) => ({
      x:
        point.x *
        monitor.correctionFactorX *
        nozzle.correctionFactorX *
        medium.correctionFactorX *
        flowRateConfig.correctionFactorX *
        pressureConfig.correctionFactorX *
        angleConfig.correctionFactorX *
        trajectoryTypeCorrectionFactorX,

      y:
        monitorHeight +
        point.y *
          monitor.correctionFactorY *
          nozzle.correctionFactorY *
          medium.correctionFactorY *
          flowRateConfig.correctionFactorY *
          pressureConfig.correctionFactorY *
          angleConfig.correctionFactorY *
          trajectoryTypeCorrectionFactorY,
    }));

    return this.removeNegativeHeightPointsFromTrajectory(trajectory);
  }

  protected removeNegativeHeightPointsFromTrajectory(trajectory: Curve): Curve {
    const firstPointBelowZeroIndex = trajectory.findIndex(
      (point) => point.y < 0
    );
    const firstPointBelowZero = trajectory[firstPointBelowZeroIndex];
    const lastPointAboveZeroIndex = firstPointBelowZeroIndex - 1;
    const lastPointAboveZero = trajectory[lastPointAboveZeroIndex];

    // If any of the points is not set (for example if there is only one point),
    // or if the last-point-above-zero is exactly at zero:
    // Cut off trajectory at first-negative-point, nothing further to do.
    if (
      !firstPointBelowZero ||
      !lastPointAboveZero ||
      lastPointAboveZero.y == 0
    ) {
      return trajectory.slice(0, firstPointBelowZeroIndex);
    }

    // Calculate the point at which a line from last-point-above-zero to first-point-below-zero collides with the ground.
    const groundCollisionPoint =
      this.collisionDetectionService.getLineSegmentColissionPoint(
        {
          start: { x: 0, y: 0 },
          end: { x: 99999, y: 0 },
        },
        {
          start: lastPointAboveZero,
          end: firstPointBelowZero,
        }
      );

    if (!groundCollisionPoint) {
      return trajectory.slice(0, firstPointBelowZeroIndex);
    }

    return [
      ...trajectory.slice(0, lastPointAboveZeroIndex),
      groundCollisionPoint,
    ];
  }

  public calculateRecoilInNewton({
    flowRateInLiterPerMinute,
    pressureInBar,
  }: CalculateRecoilInput): number {
    return Math.floor(
      Math.sqrt(
        2 *
          (pressureInBar * Math.pow(10, 8)) *
          Math.pow(flowRateInLiterPerMinute / 60000, 2)
      )
    );
  }

  public calculateImpactAreaDiameter({
    minFootprintTrajectory,
    maxFootprintTrajectory,
  }: CalculateImpactAreaInput) {
    const minImpactPoint = minFootprintTrajectory.at(-1) ?? { x: 0, y: 0 };
    const maxImpactPoint = maxFootprintTrajectory.at(-1) ?? { x: 0, y: 0 };

    return Math.abs(maxImpactPoint.x - minImpactPoint.x);
  }

  public getRangeFromTrajectories(trajectories: Curve | Array<Curve>): number {
    return Math.max(...trajectories.flat().map((point) => point.x));
  }

  public getHighestPointFromTrajectories(
    trajectories: Curve | Array<Curve>
  ): Point {
    return trajectories.flat().reduce<Point>(
      (previous, current) => {
        return previous.y > current.y ? previous : current;
      },
      { x: 0, y: 0 }
    );
  }

  public cutOffTrajectoriesAtObstacles({
    trajectoryGroup,
    obstacles,
  }: StopTrajectoriesAtObstaclesInput): boolean {
    return this.collisionDetectionService.cutTrajectoryAtObstacles({
      trajectoryGroup,
      obstacles,
    });
  }
}
